import { CalendarDays, LayoutGrid, List, ListFilter } from "lucide-react";

export type SortOption = "status" | "priority" | "time" | "list" | "manual";

export function relativeDayLabel(dateKey: string, todayKey: string) {
  const date = new Date(`${dateKey}T12:00:00`);
  const today = new Date(`${todayKey}T12:00:00`);
  const difference = Math.round((date.getTime() - today.getTime()) / 86_400_000);
  if (difference === -1) return "Yesterday";
  if (difference === 0) return "Today";
  if (difference === 1) return "Tomorrow";
  return date.toLocaleDateString(undefined, { weekday: "long" });
}

export default function DayListHeader({
  date,
  today,
  count,
  sortMode,
  onSortChange,
  filterActiveCount = 0,
  onFilterClick,
  onDateClick,
}: {
  date: string;
  today: string;
  count: number;
  sortMode: SortOption;
  onSortChange: (sort: SortOption) => void;
  filterActiveCount?: number;
  onFilterClick?: () => void;
  onDateClick?: () => void;
}) {
  const dayTitle = (
    <>
      <h2>{relativeDayLabel(date, today)}</h2>
      <span className="count">{count}</span>
    </>
  );

  return (
    <header className="day-list-header">
      {onDateClick ? (
        <button
          type="button"
          className="day-title cursor-pointer text-left transition-opacity hover:opacity-70"
          onClick={onDateClick}
          aria-label={`Open calendar details for ${relativeDayLabel(date, today)}`}
          title="Open calendar details"
        >
          {dayTitle}
        </button>
      ) : (
        <div className="day-title">{dayTitle}</div>
      )}
      <div className="list-controls">
        <div className="sort-toggle" aria-label="Sort tasks">
          <button
            type="button"
            className={sortMode === "time" ? "selected" : undefined}
            onClick={() => onSortChange("time")}
            aria-label="Sort by time"
            title="Sort by time"
            aria-pressed={sortMode === "time"}
          >
            <CalendarDays />
          </button>
          <button
            type="button"
            className={sortMode === "status" ? "selected" : undefined}
            onClick={() => onSortChange("status")}
            aria-label="Sort by status"
            title="Sort by status"
            aria-pressed={sortMode === "status"}
          >
            <List />
          </button>
          <button
            type="button"
            className={sortMode === "list" ? "selected" : undefined}
            onClick={() => onSortChange("list")}
            aria-label="Sort by list"
            title="Sort by list"
            aria-pressed={sortMode === "list"}
          >
            <LayoutGrid />
          </button>
        </div>
        {onFilterClick && (
          <button
            type="button"
            className={filterActiveCount > 0 ? "filter-button selected" : "filter-button"}
            onClick={onFilterClick}
            aria-label={filterActiveCount > 0 ? "Filter tasks, " + filterActiveCount + " active" : "Filter tasks"}
            title="Filter tasks"
            aria-pressed={filterActiveCount > 0}
          >
            <ListFilter />
            {filterActiveCount > 0 && <span className="filter-count">{filterActiveCount}</span>}
          </button>
        )}
      </div>
    </header>
  );
}
